import React from 'react';
import styled, { css } from 'styled-components';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import { blue300 } from '../../../theme/colors';
import CheckBoxList from '../../../components/CheckBoxList';
import { SwitchToggle } from '../../../components/SwitchToggle';
import CalendarComponent from './CalendarComponent';
import DatePicker from '../../../components/DatePicker';

/* ─── One filter section card ────────────────────────────── */
const FilterStyle = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  background: #ffffff;
  border-top: 1px solid rgba(203, 213, 225, 0.6);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: 1px solid rgba(203, 213, 225, 0.6);
  }
`;

/* ─── Header row ─────────────────────────────────────────── */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 14px 11px 16px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  background: ${({ isOpen }) => isOpen ? 'rgba(62,150,200,0.04)' : 'transparent'};
  border-left: ${({ isOpen }) => isOpen ? `3px solid ${blue300}` : '3px solid transparent'};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(62, 150, 200, 0.03);
  }
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ isOpen }) => isOpen ? blue300 : '#334155'};
  cursor: pointer;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s ease;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 6px;
`;

/* ─── Search input ───────────────────────────────────────── */
const SearchInput = styled.input`
  border: 1px solid rgba(62, 150, 200, 0.4);
  border-radius: 6px;
  padding: 3px 8px;
  height: 24px;
  width: ${({ show }) => show ? '100px' : '0'};
  max-width: ${({ show }) => show ? '100px' : '0'};
  opacity: ${({ show }) => show ? '1' : '0'};
  transition: width 0.22s ease, max-width 0.22s ease, opacity 0.18s ease;
  outline: none;
  font-size: 12px;
  color: #334155;
  background: #f8fafc;
  pointer-events: ${({ show }) => show ? 'auto' : 'none'};
  box-sizing: border-box;
  overflow: hidden;

  &:focus {
    border-color: ${blue300};
    background: #fff;
  }
  &::placeholder { color: #94a3b8; }
`;

/* ─── Icon button ────────────────────────────────────────── */
const IconBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s ease;

  svg {
    font-size: 15px;
    color: #94a3b8;
    transition: color 0.15s ease;
  }

  &:hover {
    background: rgba(62, 150, 200, 0.08);
    svg { color: ${blue300}; }
  }
`;

/* ─── Count badge ────────────────────────────────────────── */
const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  background: ${({ hasCount }) => hasCount ? 'rgba(62,150,200,0.12)' : '#f1f5f9'};
  color: ${({ hasCount }) => hasCount ? blue300 : '#94a3b8'};
  transition: all 0.15s ease;
`;

/* ─── Chevron ────────────────────────────────────────────── */
const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 4px;
  svg {
    font-size: 18px;
    color: #cbd5e1;
    transition: transform 0.22s ease, color 0.15s ease;
    transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
  ${({ isOpen }) => isOpen && css`svg { color: ${blue300}; }`}
`;

/* ─── Collapsible content ────────────────────────────────── */
const FilterOption = styled.div`
  max-height: ${({ showOption, maxHeight = '450px' }) => showOption ? maxHeight : '0'};
  opacity: ${({ showOption }) => showOption ? '1' : '0'};
  overflow-y: ${({ showOption }) => showOption ? 'auto' : 'hidden'};
  overflow-x: hidden;
  transition: max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
  box-sizing: border-box;
  width: 100%;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(62,150,200,0.25); border-radius: 4px; }
`;

export default FilterOption;

/* ─── Clear button ───────────────────────────────────────── */
const ClearFilterBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 16px 6px 16px;
  transition: color 0.15s ease;

  svg { font-size: 12px; }
  &:hover { color: #ef4444; }
`;

/* ─── Component ──────────────────────────────────────────── */
export class FilterWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showOption: false,
            showSearchBox: false,
            filterOption: this.props.checkboxes || [],
            options: this.props.checkedList || [],
        };
        this.searchRef = React.createRef('');
        this.calendarRef = React.createRef();
    }

    componentDidUpdate = prevProps => {
        if (this.props.checkboxes !== prevProps.checkboxes)
            this.setState({ filterOption: this.props.checkboxes });
        if (this.props.checkedList !== prevProps.checkedList)
            this.setState({ options: this.props.checkedList });
    };

    onChangeFilterValue = ({ event, target }) => {
        let { options = [] } = this.state;
        event.target.checked ? options.push(target) : options.splice(options.indexOf(target), 1);
        this.setState({ options }, () => {
            const { onChangeFilterValue, filterKey } = this.props;
            onChangeFilterValue && onChangeFilterValue(this.state.options, filterKey);
        });
    };

    searchFilter = e => {
        const { labelKey = 'name' } = this.props;
        const value = e.target.value.toLowerCase();
        const currentFilterOptions = value.trim()
            ? (this.props.checkboxes || []).filter(opt => opt[labelKey].toLowerCase().includes(value))
            : this.props.checkboxes;
        this.setState({ filterOption: currentFilterOptions });
    };

    closeSearchBox = e => {
        if (this.state.showSearchBox && e.target.value.trim() === '')
            this.setState({ showSearchBox: false });
    };

    clearFilter = () => {
        const { type } = this.props;
        if ((type === 'invoicedaterange' || type === 'qc_inbound_date_range') && this.calendarRef?.current)
            this.calendarRef.current.clearDates();

        this.setState({ options: [] }, () => {
            const { onChangeFilterValue, filterKey } = this.props;
            onChangeFilterValue && onChangeFilterValue(this.state.options, filterKey);
        });
        this.props.getSelectedDates({ dateRange: null, filterKey: this.props.filterKey });
    };

    render() {
        const { showOption, showSearchBox, options = [], filterOption = [] } = this.state;
        const {
            name, width, labelKey = 'name', valueKey = 'id', type,
            hideSearch = false, hideCount = false, filterKey,
            paymentDate, getSelectedDates, ...rest
        } = this.props;

        const selectedValueCount = Object.values(options).filter(Boolean).length;

        return (
            <FilterStyle width={width}>
                 {!rest.hideHeadingBar && (
                    <Header
                        isOpen={showOption}
                        onClick={() => {
                            const nextShowOption = !showOption;
                            this.setState({ showOption: nextShowOption });
                            if (nextShowOption && this.props.onExpand) {
                                this.props.onExpand(filterKey);
                            }
                        }}
                    >
                        <FilterLabel isOpen={showOption}>{name}</FilterLabel>

                        <HeaderRight onClick={e => e.stopPropagation()}>
                            {/* Expanding search input */}
                            {!hideSearch && (
                                <SearchInput
                                    show={showSearchBox}
                                    type="text"
                                    placeholder="Search…"
                                    onChange={this.searchFilter}
                                    ref={this.searchRef}
                                    onBlur={this.closeSearchBox}
                                />
                            )}

                            {/* Search icon */}
                            {!hideSearch && (
                                <IconBtn
                                    title="Search"
                                    onClick={() => {
                                        this.setState({ showSearchBox: true, showOption: true });
                                        if (this.props.onExpand) {
                                            this.props.onExpand(filterKey);
                                        }
                                        setTimeout(() => this.searchRef.current?.focus(), 50);
                                    }}
                                >
                                    <SearchRoundedIcon />
                                </IconBtn>
                            )}

                            {/* Count badge */}
                            {!hideCount && (
                                <CountBadge hasCount={selectedValueCount > 0}>
                                    {selectedValueCount}
                                </CountBadge>
                            )}
                        </HeaderRight>

                        {/* Chevron */}
                        <Chevron isOpen={showOption}>
                            <ExpandMoreIcon />
                        </Chevron>
                    </Header>
                )}

                {/* ── Expanded options ── */}
                {type === 'daterange' ? (
                    <FilterOption showOption={showOption} maxHeight="300px">
                        <ClearFilterBtn onClick={this.clearFilter}><ClearIcon /> Clear</ClearFilterBtn>
                        <DatePicker showCalendar name={filterKey} {...rest} />
                    </FilterOption>
                ) : (type === 'invoicedaterange' || type === 'qc_inbound_date_range') ? (
                    <FilterOption showOption={showOption} maxHeight="300px">
                        <ClearFilterBtn onClick={this.clearFilter}><ClearIcon /> Clear</ClearFilterBtn>
                        <CalendarComponent
                            ref={this.calendarRef}
                            showCalendar name={filterKey} type={type} {...rest}
                            getSelectedDates={data => {
                                this.setState({ options: [data] });
                                getSelectedDates && getSelectedDates({ dateRange: data, filterKey });
                            }}
                        />
                    </FilterOption>
                ) : type === 'toggle' ? (
                    <FilterOption showOption={showOption} maxHeight="80px">
                        <div style={{ padding: '10px 16px' }}>
                            <SwitchToggle
                                onToggle={this.onChangeFilterValue}
                                labelText={name} active={rest.value}
                                margin="0" lineWidth="2px" {...rest}
                            />
                        </div>
                    </FilterOption>
                ) : (
                    <FilterOption showOption={showOption} maxHeight="600px">
                        <ClearFilterBtn onClick={this.clearFilter}><ClearIcon /> Clear</ClearFilterBtn>
                        <CheckBoxList
                            filterName={name} labelKey={labelKey}
                            valueKey={filterKey === 'workflow_object__outlet__rsid' ? 'rsid' : valueKey}
                            options={filterOption || []}
                            onChangeFilterValue={this.onChangeFilterValue}
                            checkedList={options}
                        />
                    </FilterOption>
                )}
            </FilterStyle>
        );
    }
}